import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createLLMConfiguration, updateLLMConfiguration } from "../../api";

interface LLMConfiguration {
    id: number;
    name: string;
    provider: string;
    model_id: string;
    api_url?: string;
    api_key_set: boolean;
    temperature: number;
    max_tokens: number;
    request_timeout: number;
    custom_prompt?: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

interface LLMConfigurationEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    configuration?: LLMConfiguration | null;
}

const LLMConfigurationEditModal: React.FC<LLMConfigurationEditModalProps> = ({
    isOpen,
    onClose,
    configuration,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [name, setName] = useState("");
    const [provider, setProvider] = useState("openrouter");
    const [modelId, setModelId] = useState("");
    const [apiUrl, setApiUrl] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [temperature, setTemperature] = useState(0.7);
    const [maxTokens, setMaxTokens] = useState(1000);
    const [requestTimeout, setRequestTimeout] = useState(30);
    const [customPrompt, setCustomPrompt] = useState("");

    // Default prompts for each provider
    const defaultPrompts = {
        openrouter: `You are a comic book expert. Extract information from the following content and return a JSON object.

Content: {content}

{language_constraint}{tag_suggestions}{role_suggestions}

Return a JSON object with this exact structure:
{{
    "title": "Comic title",
    "series": "Series name",
    "issue_number": "Issue number if available",
    "publication_date": "Date in YYYY-MM-DD format",
    "language": "Language",
    "notes": "Description or notes",
    "tags": ["tag1", "tag2"],
    "people": [
        {{"name": "Person Name", "role": "Role"}}
    ]
}}

Return only valid JSON, no additional text.`,
        ollama: `Extract comic book information from the content below and return as JSON.

Content: {content}

{language_constraint}{tag_suggestions}{role_suggestions}

Respond with JSON only:
{{
    "title": "Comic title",
    "series": "Series name", 
    "issue_number": "Issue number",
    "publication_date": "YYYY-MM-DD",
    "language": "Language",
    "notes": "Description",
    "tags": ["tag1", "tag2"],
    "people": [{{"name": "Name", "role": "Role"}}]
}}`,
        openai_compatible: `You are a comic book expert. Extract information from the provided content and return it as a JSON object.

Content: {content}

{language_constraint}{tag_suggestions}{role_suggestions}

Return a JSON object with this structure:
{{
    "title": "Comic title",
    "series": "Series name",
    "issue_number": "Issue number if available", 
    "publication_date": "Date in YYYY-MM-DD format",
    "language": "Language",
    "notes": "Description or notes",
    "tags": ["tag1", "tag2"],
    "people": [
        {{"name": "Person Name", "role": "Role"}}
    ]
}}

Return only the JSON object, no additional text.`
    };

    // Default models for each provider
    const defaultModels = {
        openrouter: "meta-llama/llama-3.1-8b-instruct:free",
        ollama: "llama3.1",
        openai_compatible: "gpt-4o-mini"
    };

    // Default API URLs for each provider
    const defaultApiUrls = {
        openrouter: "https://openrouter.ai/api/v1",
        ollama: "http://localhost:11434",
        openai_compatible: "https://api.openai.com/v1"
    };

    const resetForm = useCallback(() => {
        setName("");
        setProvider("openrouter");
        setModelId(defaultModels.openrouter);
        setApiUrl(defaultApiUrls.openrouter);
        setApiKey("");
        setTemperature(0.7);
        setMaxTokens(1000);
        setRequestTimeout(30);
        setCustomPrompt(defaultPrompts.openrouter);
    }, [defaultModels.openrouter, defaultApiUrls.openrouter, defaultPrompts.openrouter]);

    useEffect(() => {
        if (isOpen) {
            if (configuration) {
                // Editing existing configuration
                setName(configuration.name);
                setProvider(configuration.provider);
                setModelId(configuration.model_id);
                setApiUrl(configuration.api_url || "");
                setApiKey(""); // Don't pre-fill API key for security
                setTemperature(configuration.temperature);
                setMaxTokens(configuration.max_tokens);
                setRequestTimeout(configuration.request_timeout);
                setCustomPrompt(configuration.custom_prompt || "");
            } else {
                // Adding new configuration
                resetForm();
            }
            setError(null);
        }
    }, [isOpen, configuration, resetForm]);


    const handleProviderChange = (newProvider: string) => {
        setProvider(newProvider);
        setModelId(defaultModels[newProvider as keyof typeof defaultModels]);
        setApiUrl(defaultApiUrls[newProvider as keyof typeof defaultApiUrls]);
        setCustomPrompt(defaultPrompts[newProvider as keyof typeof defaultPrompts]);
        
        // Adjust default timeout for Ollama
        if (newProvider === "ollama") {
            setRequestTimeout(60);
        } else {
            setRequestTimeout(30);
        }
    };

    const generateDefaultName = () => {
        const providerNames = {
            openrouter: "OpenRouter",
            ollama: "Ollama",
            openai_compatible: "OpenAI"
        };
        
        const providerName = providerNames[provider as keyof typeof providerNames];
        const model = modelId || "Unknown";
        return `${providerName} - ${model}`;
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);

        try {
            // Validation
            if (!name.trim()) {
                throw new Error(t('errors.configurationNameRequired'));
            }
            if (!modelId.trim()) {
                throw new Error(t('errors.modelIdRequired'));
            }

            const configData = {
                name: name.trim(),
                provider,
                model_id: modelId.trim(),
                api_url: apiUrl.trim() || undefined,
                api_key: apiKey.trim() || undefined,
                temperature,
                max_tokens: maxTokens,
                request_timeout: requestTimeout,
                custom_prompt: customPrompt.trim() || undefined
            };

            if (configuration) {
                await updateLLMConfiguration(configuration.id, configData);
            } else {
                await createLLMConfiguration(configData);
            }

            onClose();
        } catch (err) {
            
            setError(err instanceof Error ? err.message : t('errors.failedToSaveConfiguration'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscapeKey);
        }

        return () => {
            document.removeEventListener("keydown", handleEscapeKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isEditing = !!configuration;

    return (
        <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-[70]">
            <div className="relative top-10 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-[700px] shadow-lg rounded-md bg-white dark:bg-gray-800">
                <div className="mt-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                        {isEditing ? t('llm.configurations.editConfiguration') : t('llm.configurations.addConfiguration')}
                    </h3>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                        {/* Configuration Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('llm.configurations.configurationName')}
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={t('llm.configurations.configurationNamePlaceholder')}
                                />
                                <button
                                    type="button"
                                    onClick={() => setName(generateDefaultName())}
                                    className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                >
                                    {t('llm.configurations.autoGenerate')}
                                </button>
                            </div>
                        </div>

                        {/* Provider Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('llm.configurations.provider')}
                            </label>
                            <select
                                value={provider}
                                onChange={(e) => handleProviderChange(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="openrouter">{t('llm.configurations.providerOpenRouter')}</option>
                                <option value="ollama">{t('llm.configurations.providerOllama')}</option>
                                <option value="openai_compatible">{t('llm.configurations.providerOpenAI')}</option>
                            </select>
                        </div>

                        {/* Model ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('llm.configurations.modelId')}
                            </label>
                            <input
                                type="text"
                                value={modelId}
                                onChange={(e) => setModelId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('llm.configurations.modelIdPlaceholder')}
                            />
                        </div>

                        {/* API URL */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('llm.configurations.apiUrl')}
                            </label>
                            <input
                                type="text"
                                value={apiUrl}
                                onChange={(e) => setApiUrl(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('llm.configurations.apiUrlPlaceholder')}
                            />
                        </div>

                        {/* API Key */}
                        {(provider === 'openrouter' || provider === 'openai_compatible') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('llm.configurations.apiKey')}
                                </label>
                                <input
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={isEditing ? t('llm.configurations.apiKeyPlaceholderEdit') : t('placeholder.enterApiKey')}
                                />
                                {isEditing && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {t('llm.configurations.apiKeyKeepCurrent')}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Model Parameters */}
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('llm.temperature')}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="2"
                                    step="0.1"
                                    value={temperature}
                                    onChange={(e) => setTemperature(parseFloat(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('llm.maxTokens')}
                                </label>
                                <input
                                    type="number"
                                    min="100"
                                    max="4000"
                                    value={maxTokens}
                                    onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {t('llm.configurations.timeout')}
                                </label>
                                <input
                                    type="number"
                                    min="10"
                                    max="300"
                                    value={requestTimeout}
                                    onChange={(e) => setRequestTimeout(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </div>


                        {/* Custom Prompt */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {t('llm.configurations.customPrompt')}
                            </label>
                            <textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={t('llm.configurations.customPromptPlaceholder')}
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t('llm.configurations.customPromptHelp')}
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                        >
                            {loading ? t('common.saving') : (isEditing ? t('common.update') : t('common.create'))}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LLMConfigurationEditModal;